import { FluentClickHandler } from '@h2oai/ui-kit';
import { useEffect, useRef } from 'react';

import { EnvService } from '../../../services/api';
import type { EnvironmentAndMenu } from '../../../utils/contexts';
import { useBanner } from '../../../utils/hooks';
import { createBookTimeBanner, getMessageBarType } from '../helpers/notifications';

/**
 * Loads and displays environment notices and book time banner
 */
export const useNotices = (env: EnvironmentAndMenu | undefined, onClickBookTime: FluentClickHandler) => {
  const { add: addBanner } = useBanner();
  const envRef = useRef(env);
  const onClickBookTimeRef = useRef(onClickBookTime);
  const addBannerRef = useRef(addBanner);

  // Keep refs up to date to avoid stale closures
  envRef.current = env;
  onClickBookTimeRef.current = onClickBookTime;
  addBannerRef.current = addBanner;

  useEffect(() => {
    const loadNotices = async () => {
      try {
        const { notices } = await EnvService.listNotices({});

        notices.forEach(({ title, severity, content }, idx) => {
          // TODO: the current `id` field is not optimal, but we count on a fact that notices are fetched on an app load
          // only although React does not gives you strong guarantee that the `useEffect` will be called only once.
          // The notices should be loaded periodically anyway, to show new notices without a need of app reload and then
          // the `id` derived from position would not work properly. We need to synchronize the notices with the banners,
          // thus if some notice is missing, remove it from the banners list, if it is new, add it, if it is updated,
          // update it. For that, some kind of backend identifier would be optimal.
          addBannerRef.current({
            id: `notice-${idx}`,
            messageBarType: getMessageBarType(severity),
            message: `${title ? `${title}, ` : ''}${content}`,
          });
        });

        if (envRef.current?.menu?.hasBookTime) {
          addBannerRef.current(createBookTimeBanner(onClickBookTimeRef.current));
        }
      } catch (error) {
        console.error('Failed to load notices:', error);
      }
    };

    loadNotices();
  }, [env?.menu?.hasBookTime]);
};
